import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, Users, TrendingUp, Copy, Check, ArrowLeft, BarChart3, Activity, Clock, Target, Award, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Line, Doughnut } from 'react-chartjs-2';
import useAffiliateConfig from '../hooks/useAffiliateConfig';
import LevelBadge from '../components/affiliate/LevelBadge';
import LevelProgress from '../components/affiliate/LevelProgress';
import LevelHistory from '../components/affiliate/LevelHistory';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { y: 30, opacity: 0, scale: 0.95 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};

const glowVariants = {
  animate: {
    boxShadow: [
      '0 0 20px rgba(251, 191, 36, 0.3)',
      '0 0 40px rgba(251, 191, 36, 0.5)',
      '0 0 20px rgba(251, 191, 36, 0.3)'
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
};

const AffiliateDashboardAwwwards = ({ embedded = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [errorState, setErrorState] = useState(null); // 'not_affiliate' | 'network_error' | null
  const [retryCount, setRetryCount] = useState(0);
  const { commissionRate, commissionPerSale } = useAffiliateConfig();

  useEffect(() => {
    // Check if user is logged in (skip redirect if embedded)
    if (!user && !embedded) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchDashboard();
    }
  }, [user, retryCount, embedded]);

  const fetchDashboard = async () => {
    try {
      setErrorState(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/affiliate/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
        setErrorState(null);
      } else if (response.status === 403 || response.status === 404) {
        // User is not an affiliate - show invitation screen
        setErrorState('not_affiliate');
      } else {
        // Other errors (500, etc.) - show retry screen
        setErrorState('network_error');
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      // Network error - show retry screen
      setErrorState('network_error');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setRetryCount(prev => prev + 1);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(dashboard.affiliate_link);
    setCopied(true);
    toast.success('🎉 Lien copié dans le presse-papier !');
    setTimeout(() => setCopied(false), 3000);
  };

  // Calculate conversion rate
  const conversionRate = dashboard 
    ? dashboard.stats.total_referrals > 0
      ? ((dashboard.stats.paid_referrals / dashboard.stats.total_referrals) * 100).toFixed(1)
      : 0
    : 0;

  // Real data for performance chart based on actual referrals
  const performanceData = dashboard ? {
    labels: ['Total', 'Conversions Payantes', 'En Attente'],
    datasets: [
      {
        label: 'Filleuls',
        data: [
          dashboard.stats.total_referrals, 
          dashboard.stats.paid_referrals,
          dashboard.stats.total_referrals - dashboard.stats.paid_referrals
        ],
        borderColor: 'rgb(147, 112, 219)', // Medium Purple
        backgroundColor: 'rgba(147, 112, 219, 0.15)',
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgb(147, 112, 219)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }
    ]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        borderRadius: 8,
        titleColor: '#fff',
        bodyColor: '#fbbf24'
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          borderDash: [5, 5]
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Doughnut chart for referral status
  const doughnutData = dashboard ? {
    labels: ['Payants', 'Gratuits'],
    datasets: [
      {
        data: [dashboard.stats.paid_referrals, dashboard.stats.total_referrals - dashboard.stats.paid_referrals],
        backgroundColor: ['#10b981', '#6b7280'],
        borderWidth: 0,
        cutout: '70%'
      }
    ]
  } : null;

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        borderRadius: 8
      }
    }
  };

  if (loading) {
    return (
      <div className={`${embedded ? 'py-12' : 'min-h-screen'} bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center ${embedded ? 'rounded-2xl' : ''}`}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1, rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Not affiliate screen - elegant invitation
  if (errorState === 'not_affiliate') {
    return (
      <div className={`${embedded ? 'py-12' : 'min-h-screen'} bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 ${embedded ? 'rounded-2xl' : ''}`}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg w-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-10 border border-white/20 text-center relative overflow-hidden"
        >
          {/* Decorative glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl" />
          
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, duration: 0.6, type: 'spring' }}
            className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 mb-6 shadow-2xl"
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-white mb-4"
          >
            Tu y es presque ✨
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-gray-300 text-lg leading-relaxed mb-3"
          >
            Ton accès affilié n'est pas encore activé.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-400 mb-8 leading-relaxed"
          >
            Une opportunité parfaite pour rejoindre officiellement le programme et commencer à gagner en partageant Coach DIA.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <button
              onClick={() => navigate('/affiliation')}
              className="w-full px-8 py-4 bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 text-white font-bold rounded-2xl hover:shadow-2xl hover:scale-105 transition-all duration-300 shadow-xl"
              style={{ boxShadow: '0 10px 40px rgba(251, 191, 36, 0.4)' }}
            >
              Rejoindre le Programme Affilié
            </button>

            <button
              onClick={() => navigate('/training')}
              className="w-full px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-2xl transition-all duration-300"
            >
              Retour au Dashboard
            </button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Network error screen - with retry
  if (errorState === 'network_error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 0.5, repeat: 3 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/20 mb-4"
          >
            <Activity className="w-8 h-8 text-orange-400" />
          </motion.div>

          <h3 className="text-white text-xl font-bold mb-2">Connexion temporaire</h3>
          <p className="text-gray-400 mb-6">
            Impossible de charger les données. Vérifie ta connexion et réessaie.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl transition-all duration-300"
            >
              Réessayer
            </button>

            <button
              onClick={() => navigate('/training')}
              className="w-full px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
            >
              Retour
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // No dashboard data yet (should not happen with proper error handling)
  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1, rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'rounded-2xl' : 'min-h-screen'} bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4 overflow-hidden ${embedded ? 'relative' : ''}`}>
      {/* Animated background gradient orbs */}
      {!embedded && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 100, 0],
              y: [0, 50, 0]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute -top-40 -left-40 w-80 h-80 bg-amber-500/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              x: [0, -100, 0],
              y: [0, -50, 0]
            }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
            className="absolute -bottom-40 -right-40 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
          />
        </div>
      )}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto relative z-10"
      >
        {/* Pending Approval Banner */}
        {dashboard.affiliate_status === 'pending' && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-lg rounded-2xl p-6 border border-amber-400/40"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-500/30 rounded-xl">
                <Clock className="w-6 h-6 text-amber-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-amber-300 mb-2">
                  ⏳ Demande en attente d'approbation
                </h3>
                <p className="text-amber-100/80 text-sm mb-3">
                  Votre demande d'affiliation a été reçue ! Un administrateur va l'examiner prochainement. 
                  Vous recevrez un email dès qu'elle sera approuvée.
                </p>
                <div className="flex items-center gap-2 text-xs text-amber-200/60">
                  <span className="px-3 py-1 bg-amber-500/20 rounded-full">
                    ✓ Formulaire soumis
                  </span>
                  <span className="px-3 py-1 bg-gray-500/20 rounded-full">
                    ⏳ En cours de validation
                  </span>
                  <span className="px-3 py-1 bg-gray-500/20 rounded-full">
                    📧 Notification email
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Rejected Status Banner */}
        {dashboard.affiliate_status === 'rejected' && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-6 bg-gradient-to-r from-red-500/20 to-rose-500/20 backdrop-blur-lg rounded-2xl p-6 border border-red-400/40"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-500/30 rounded-xl">
                <Activity className="w-6 h-6 text-red-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-300 mb-2">
                  ❌ Demande rejetée
                </h3>
                <p className="text-red-100/80 text-sm mb-3">
                  Malheureusement, votre demande d'affiliation n'a pas été approuvée. 
                  Contactez l'administrateur pour plus d'informations.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Header with glassmorphism */}
        <motion.div variants={cardVariants} className="flex items-center justify-between mb-8">
          <div>
            {!embedded && (
              <button
                onClick={() => navigate('/training')}
                className="flex items-center gap-2 text-gray-400 hover:text-white mb-3 transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span>Retour</span>
              </button>
            )}
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-amber-400" />
              <div>
                <h1 className="text-4xl font-bold text-white bg-clip-text bg-gradient-to-r from-amber-400 to-amber-200">
                  Dashboard Affilié
                </h1>
                <p className="text-gray-400 mt-1">
                  Code: <span className="font-mono text-amber-400 font-semibold">{dashboard.affiliate_code}</span>
                </p>
              </div>
            </div>
          </div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="text-right bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10"
          >
            <p className="text-sm text-gray-400">Taux de Commission</p>
            <p className="text-3xl font-bold text-amber-400">{dashboard.commission_rate}%</p>
            <p className="text-xs text-gray-500 mt-1">{(29 * dashboard.commission_rate / 100).toFixed(2)}€ par vente</p>
          </motion.div>
        </motion.div>

        {/* Stats Cards with enhanced design */}
        <motion.div variants={cardVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Referrals */}
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            className="relative bg-gradient-to-br from-blue-500/20 to-blue-600/10 backdrop-blur-lg rounded-2xl p-6 border border-blue-400/20 overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-blue-500/20 rounded-xl">
                  <Users className="w-7 h-7 text-blue-400" />
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, type: 'spring' }}
                  className="px-3 py-1 bg-blue-500/20 rounded-full"
                >
                  <span className="text-xs text-blue-300 font-semibold">Total</span>
                </motion.div>
              </div>
              <p className="text-4xl font-bold text-white mb-1">{dashboard.stats.total_referrals}</p>
              <p className="text-blue-300 text-sm">Filleuls Inscrits</p>
            </div>
          </motion.div>

          {/* Paid Referrals */}
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            className="relative bg-gradient-to-br from-green-500/20 to-green-600/10 backdrop-blur-lg rounded-2xl p-6 border border-green-400/20 overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 via-green-500/10 to-green-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-green-500/20 rounded-xl">
                  <TrendingUp className="w-7 h-7 text-green-400" />
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6, type: 'spring' }}
                  className="px-3 py-1 bg-green-500/20 rounded-full"
                >
                  <span className="text-xs text-green-300 font-semibold">Actifs</span>
                </motion.div>
              </div>
              <p className="text-4xl font-bold text-green-400 mb-1">{dashboard.stats.paid_referrals}</p>
              <p className="text-green-300 text-sm">Filleuls Payants</p>
            </div>
          </motion.div>

          {/* Total Earned */}
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            variants={glowVariants}
            animate="animate"
            className="relative bg-gradient-to-br from-amber-500/20 to-amber-600/10 backdrop-blur-lg rounded-2xl p-6 border border-amber-400/30 overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/20 to-amber-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-amber-500/20 rounded-xl">
                  <DollarSign className="w-7 h-7 text-amber-400" />
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.7, type: 'spring' }}
                  className="px-3 py-1 bg-amber-500/30 rounded-full"
                >
                  <span className="text-xs text-amber-300 font-semibold">Cumulé</span>
                </motion.div>
              </div>
              <p className="text-4xl font-bold text-amber-400 mb-1">{dashboard.stats.total_earned.toFixed(2)}€</p>
              <p className="text-amber-300 text-sm">Total Gagné</p>
            </div>
          </motion.div>

          {/* Pending Commission */}
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            className="relative bg-gradient-to-br from-purple-500/20 to-purple-600/10 backdrop-blur-lg rounded-2xl p-6 border border-purple-400/20 overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/10 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-purple-500/20 rounded-xl">
                  <Clock className="w-7 h-7 text-purple-400" />
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.8, type: 'spring' }}
                  className="px-3 py-1 bg-purple-500/20 rounded-full"
                >
                  <span className="text-xs text-purple-300 font-semibold">En attente</span>
                </motion.div>
              </div>
              <p className="text-4xl font-bold text-purple-400 mb-1">{dashboard.stats.pending_commission.toFixed(2)}€</p>
              <p className="text-purple-300 text-sm">À Recevoir</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Level System Section */}
        <motion.div variants={cardVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Current Level Badge */}
          <motion.div
            whileHover={{ y: -5 }}
            className="relative bg-gradient-to-br from-amber-500/10 via-purple-500/10 to-amber-500/10 backdrop-blur-xl rounded-3xl p-8 border border-amber-400/30 flex flex-col items-center justify-center text-center"
          >
            <h3 className="text-xl font-bold text-white mb-6">Ton Niveau Actuel</h3>
            {dashboard.current_level && (
              <LevelBadge 
                level={{
                  name: dashboard.current_level.name,
                  badge: dashboard.current_level.badge,
                  commission_rate: dashboard.current_level.commission_rate
                }} 
                size="large" 
              />
            )}
          </motion.div>

          {/* Level Progress */}
          <div className="lg:col-span-2">
            <LevelProgress
              currentLevel={dashboard.current_level}
              nextLevel={dashboard.next_level}
              paidReferrals={dashboard.stats.paid_referrals}
            />
          </div>
        </motion.div>

        {/* Level History */}
        {dashboard.level_history && dashboard.level_history.length > 0 && (
          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-3xl p-8 border border-purple-400/30 mb-8"
          >
            <LevelHistory history={dashboard.level_history} />
          </motion.div>
        )}

        {/* Affiliate Link - Premium Glassmorphism */}
        <motion.div
          variants={cardVariants}
          whileHover={{ scale: 1.01 }}
          className="relative bg-gradient-to-r from-amber-500/10 via-purple-500/10 to-amber-500/10 backdrop-blur-xl rounded-3xl p-8 border border-amber-400/30 mb-8 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-purple-500/5 opacity-50" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-500/20 rounded-xl">
                <Award className="w-6 h-6 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Votre Lien d'Affiliation Personnel</h2>
            </div>
            <div className="flex gap-4 items-center flex-wrap md:flex-nowrap">
              <input
                type="text"
                value={dashboard.affiliate_link}
                readOnly
                className="flex-1 w-full md:w-auto px-6 py-4 rounded-2xl border text-sm backdrop-blur-sm focus:outline-none transition-colors overflow-x-auto"
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderColor: 'rgba(251, 191, 36, 0.4)',
                  color: '#FFFFFF',
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap',
                  minWidth: '300px'
                }}
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={copyLink}
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-2xl hover:from-amber-600 hover:to-amber-700 flex items-center gap-3 transition-all shadow-lg shadow-amber-500/30"
              >
                <AnimatePresence mode="wait">
                  {copied ? (
                    <motion.div
                      key="check"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0 }}
                      className="flex items-center gap-2"
                    >
                      <Check className="w-5 h-5" />
                      <span>Copié !</span>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="copy"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="flex items-center gap-2"
                    >
                      <Copy className="w-5 h-5" />
                      <span>Copier</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-gray-300 text-sm mt-4 flex items-start gap-2"
            >
              <Sparkles className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <span>
                Partagez ce lien unique sur vos réseaux sociaux, blog ou newsletter. 
                Vous gagnerez <span className="font-semibold text-amber-400">{dashboard.commission_rate}%</span> de commission 
                sur chaque abonnement premium via votre lien.
              </span>
            </motion.p>
          </div>
        </motion.div>

        {/* Charts Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Performance Chart */}
          <motion.div
            variants={cardVariants}
            className="md:col-span-2 bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-500/20 rounded-xl">
                <BarChart3 className="w-6 h-6 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Performance des 7 derniers jours</h2>
            </div>
            <div className="h-64">
              <Line data={performanceData} options={chartOptions} />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-400">{conversionRate}%</p>
                <p className="text-sm text-gray-400">Taux de Conversion</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">+{dashboard.stats.paid_referrals}</p>
                <p className="text-sm text-gray-400">Conversions Totales</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-400">{dashboard.stats.total_referrals}</p>
                <p className="text-sm text-gray-400">Clics Totaux</p>
              </div>
            </div>
          </motion.div>

          {/* Referral Status Doughnut */}
          <motion.div
            variants={cardVariants}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-500/20 rounded-xl">
                <Target className="w-6 h-6 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Répartition</h2>
            </div>
            <div className="h-48 flex items-center justify-center mb-4">
              {doughnutData && <Doughnut data={doughnutData} options={doughnutOptions} />}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-white text-sm">Payants</span>
                </div>
                <span className="text-green-400 font-bold">{dashboard.stats.paid_referrals}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-500/10 rounded-xl border border-gray-500/20">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-500" />
                  <span className="text-white text-sm">Gratuits</span>
                </div>
                <span className="text-gray-400 font-bold">{dashboard.stats.total_referrals - dashboard.stats.paid_referrals}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Recent Referrals with Timeline */}
          <motion.div
            variants={cardVariants}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-500/20 rounded-xl">
                <Activity className="w-6 h-6 text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Activité Récente</h2>
            </div>
            {dashboard.recent_referrals.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Aucun filleul pour le moment</p>
                <p className="text-gray-500 text-sm mt-2">Partagez votre lien pour commencer !</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dashboard.recent_referrals.map((referral, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="relative bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors group"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-purple-400 rounded-l-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-white font-medium">{referral.name || 'Sans nom'}</p>
                        <p className="text-gray-400 text-sm">{referral.email}</p>
                      </div>
                      <div className="text-right">
                        {referral.plan === 'premium' ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                            ⭐ Premium
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">
                            Gratuit
                          </span>
                        )}
                        <p className="text-gray-500 text-xs mt-1">
                          {new Date(referral.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Earnings Info */}
          <motion.div
            variants={cardVariants}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-500/20 rounded-xl">
                <DollarSign className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Informations de Paiement</h2>
            </div>
            
            <div className="space-y-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-r from-white/5 to-white/10 rounded-xl p-4 border border-white/10"
              >
                <p className="text-gray-400 text-sm mb-2">Méthode de paiement</p>
                <p className="text-white font-semibold text-lg">
                  {dashboard.payment_method === 'bank_transfer' ? '🏦 Virement bancaire' : '💳 PayPal'}
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-r from-white/5 to-white/10 rounded-xl p-4 border border-white/10"
              >
                <p className="text-gray-400 text-sm mb-2">Seuil minimum de retrait</p>
                <p className="text-white font-semibold text-lg">{dashboard.payment_threshold.toFixed(2)}€</p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 rounded-xl p-4 border border-amber-500/30"
              >
                <p className="text-amber-300 text-sm mb-2">Commission par vente</p>
                <p className="text-amber-400 font-bold text-2xl">
                  {(29 * dashboard.commission_rate / 100).toFixed(2)}€
                </p>
                <p className="text-amber-200/60 text-xs mt-1">
                  22,90€ × {dashboard.commission_rate}% = {(22.90 * dashboard.commission_rate / 100).toFixed(2)}€
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-xl p-4 border border-green-500/30"
              >
                <p className="text-green-300 text-sm mb-2">Total versé</p>
                <p className="text-green-400 font-bold text-2xl">
                  {dashboard.stats.total_paid.toFixed(2)}€
                </p>
              </motion.div>

              {dashboard.stats.pending_commission >= dashboard.payment_threshold && (
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/50 rounded-xl p-4"
                >
                  <p className="text-amber-300 font-semibold mb-2 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Paiement disponible
                  </p>
                  <p className="text-amber-200 text-sm">
                    Vous avez atteint le seuil minimum. Le paiement sera traité prochainement.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default AffiliateDashboardAwwwards;
